import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  Phone, MessageCircle, Send, X, Minimize2, User, 
  Clock, Calendar, AlertCircle, CheckCircle2, Loader2,
  Mic, Volume2, Home
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { ShieldLogo } from "../components/landing/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Quick action buttons for common intents
const QUICK_ACTIONS = [
  { label: "Make a Reservation", icon: Calendar, prompt: "I'd like to make a reservation" },
  { label: "View Menu", icon: MessageCircle, prompt: "What's on your menu?" },
  { label: "Dietary Options", icon: AlertCircle, prompt: "Do you have gluten-free options?" },
  { label: "Hours & Location", icon: Clock, prompt: "What are your hours?" },
];

// Message bubble component
const MessageBubble = ({ message, isUser, sentiment }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3`}
  >
    {!isUser && (
      <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center mr-2 shrink-0">
        <Phone className="w-4 h-4 text-gold" />
      </div>
    )}
    <div className={`max-w-[80%] rounded-xl px-4 py-3 ${
      isUser 
        ? "bg-indigo-500/30 text-white border border-indigo-400/20" 
        : "bg-[#0A1244] text-slate-200 border border-gold/15"
    }`}>
      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
      <div className="flex items-center gap-2 mt-1">
        {message.intent && !isUser && (
          <span className="text-[10px] text-gold/60">
            {message.intent.replace("_", " ")}
          </span>
        )}
        {sentiment && !isUser && (
          <span className={`text-[10px] px-1.5 py-0.5 rounded ${
            sentiment.escalation_triggered ? "bg-red-500/20 text-red-400" :
            sentiment.agitation_score >= 0.4 ? "bg-amber-500/20 text-amber-400" :
            "bg-green-500/20 text-green-400"
          }`}>
            {sentiment.mood}
          </span>
        )}
      </div>
      {message.requires_human && (
        <div className="flex items-center gap-1 mt-2 text-amber-400 text-xs">
          <AlertCircle className="w-3 h-3" />
          <span>Manager notified</span>
        </div>
      )}
    </div>
  </motion.div>
);

// Main Receptionist Page
export default function ReceptionistPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  
  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);
  
  // Initialize with greeting
  useEffect(() => {
    setMessages([{
      role: "assistant",
      content: "Welcome to Argus Xenia! I'm your 24/7 AI receptionist. How can I help you today?\n\nI can assist with reservations, menu questions, dietary requirements, or general inquiries.",
      intent: "greeting"
    }]);
  }, []);
  
  const sendMessage = async (text) => {
    if (!text.trim() || isTyping) return;
    
    const userMsg = { role: "user", content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setIsTyping(true);
    
    try {
      const res = await axios.post(`${API}/receptionist/message`, {
        message: text,
        channel: "web",
        session_id: sessionId
      });
      
      if (!sessionId) {
        setSessionId(res.data.session_id);
      }
      
      const assistantMsg = {
        role: "assistant",
        content: res.data.response,
        intent: res.data.intent_detected,
        requires_human: res.data.requires_human,
        sentiment: res.data.sentiment
      };
      
      setMessages(prev => [...prev, assistantMsg]);
      
      if (res.data.requires_human) {
        toast.info("A staff member will follow up with you shortly.");
      }
      
      // Show warning if escalation was triggered
      if (res.data.sentiment?.escalation_triggered) {
        toast.warning("A manager has been alerted to assist you.", { duration: 5000 });
      }
      
    } catch (err) {
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "I apologize, I'm having a moment. Please try again or call us directly.",
        intent: "error"
      }]);
    } finally {
      setIsTyping(false);
    }
  };
  
  const handleQuickAction = (prompt) => {
    sendMessage(prompt);
  };
  
  return (
    <div className="min-h-screen bg-[#040F49] flex flex-col" data-testid="receptionist-page">
      <div className="noise-overlay" />
      
      {/* Header */}
      <div className="sticky top-0 z-40 backdrop-blur-xl bg-indigo-700/90 border-b border-gold/20">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldLogo size="sm" />
            <div>
              <span className="font-inter text-sm font-bold text-gold tracking-wider">24/7 AI RECEPTIONIST</span>
              <span className="ml-2 px-2 py-0.5 rounded-full bg-lime/20 text-lime text-[10px] font-bold">
                The Ears
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-lime text-xs">
              <CheckCircle2 className="w-3 h-3" />
              <span>Online</span>
            </div>
            <a href="/" className="text-slate-400 hover:text-white">
              <Home className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
      
      {/* Main Chat Area */}
      <div className="flex-1 max-w-3xl w-full mx-auto flex flex-col">
        {/* Quick Actions */}
        <div className="px-6 py-4 border-b border-gold/10">
          <p className="text-xs text-slate-500 mb-2">Quick actions:</p>
          <div className="flex flex-wrap gap-2">
            {QUICK_ACTIONS.map((action, idx) => (
              <button
                key={idx}
                onClick={() => handleQuickAction(action.prompt)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-700/50 border border-gold/20 text-xs text-slate-300 hover:border-gold/50 hover:text-gold transition-colors"
              >
                <action.icon className="w-3 h-3" />
                {action.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {messages.map((msg, idx) => (
            <MessageBubble key={idx} message={msg} isUser={msg.role === "user"} sentiment={msg.sentiment} />
          ))}
          
          {isTyping && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center gap-2 text-slate-500"
            >
              <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center">
                <Phone className="w-4 h-4 text-gold" />
              </div>
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-gold/50 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-2 h-2 bg-gold/50 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-2 h-2 bg-gold/50 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </motion.div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        {/* Input */}
        <div className="px-6 py-4 border-t border-gold/10 bg-[#040F49]">
          <form
            onSubmit={(e) => { e.preventDefault(); sendMessage(input); }}
            className="flex gap-2"
          >
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message..."
              className="flex-1 bg-indigo-700/50 border-gold/20 text-white placeholder:text-slate-500"
              disabled={isTyping}
              data-testid="receptionist-input"
            />
            <Button
              type="submit"
              disabled={!input.trim() || isTyping}
              className="bg-gold hover:bg-gold/80 text-indigo-700"
              data-testid="receptionist-send"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
          <p className="text-[10px] text-slate-600 mt-2 text-center">
            Powered by Argus Xenia AI • Real-time menu & inventory data
          </p>
        </div>
      </div>
    </div>
  );
}
